/**
 * How an inspection submission is named on screen.
 *
 * WHY THIS IS SHARED AND NOT LOCAL
 * --------------------------------
 * There were already two spellings of the same six-entry map when the field
 * screen needed a third: a `Record` in InspectionDetailPage.tsx, and a chain of
 * six nested ternaries inline in SafetyRecordsScreen.tsx's Inspections table.
 * They agreed, by hand. A seventh form type would have had to be added in three
 * places, and the failure of missing one is not a crash — it is a row reading
 * `rotary_slips_l2` on the office table while the field screen and the PDF say
 * "Rotary Slips L2", and nobody being sure which screen is stale.
 *
 * EXHAUSTIVE MAP, NOT A TRANSFORM. `replace(/_/g, ' ')` would render five of
 * the six acceptably and turn `crti` into "crti" and `slip_elevator_l1` into
 * "slip elevator l1". The house rule is a map keyed on every enum value with no
 * clever fallback branch — see CLAUDE.md, "Label maps must be exhaustive".
 */
export const INSPECTION_FORM_TYPE_LABELS: Record<string, string> = {
  power_tong: 'Power Tong',
  crti: 'CRTi',
  elevators: 'Elevators',
  slip_elevator_l1: 'Slip Elevator L1',
  rotary_slips_l1: 'Rotary Slips L1',
  tong_truck: 'Tong Truck',
};

/**
 * The label for one submission's form type.
 *
 * Falls back to the template's own name before the raw key, because a tenant
 * template genuinely carries a name worth showing. The raw key is the last
 * resort and is preferred to a dash: an unmapped type is a gap in this file,
 * and printing the key is how someone finds out which one to add.
 */
export function inspectionFormTypeLabel(submission: {
  form_type?: string | null;
  formType?: string | null;
  template_name?: string | null;
  templateName?: string | null;
}): string {
  const key = submission.form_type ?? submission.formType ?? '';
  return (
    INSPECTION_FORM_TYPE_LABELS[key] ||
    submission.template_name ||
    submission.templateName ||
    key ||
    'Inspection'
  );
}

/**
 * What a row's `status` means to the person who filed it.
 *
 * THREE STATES, NOT TWO, AND THE THIRD IS THE ONE WORTH HAVING. The form mints
 * a `draft` row the moment a type and an equipment are picked, so simply
 * opening the form and walking away leaves one behind with `results = {}`.
 * Every draft in production today is that shape. Calling those "Draft" invites
 * a hand to reopen a form they never started; calling them what they are —
 * nothing recorded yet — costs one word and tells the truth.
 *
 * The predicate matches the API's own definition of an abandoned draft
 * (`inspection-draft-cleanup.service.ts`: status draft AND `results = '{}'`),
 * so the row this labels "Not started" is exactly the row the nightly worker
 * will sweep. It is a LABEL, not a filter — nothing is hidden on the strength
 * of it, which is what would make a client-side copy of a server predicate
 * dangerous.
 */
export function inspectionStatusLabel(submission: {
  status?: string | null;
  results?: unknown;
}): 'Submitted' | 'Draft' | 'Not started' {
  if (submission.status !== 'draft') return 'Submitted';
  const results = submission.results;
  const started =
    !!results && typeof results === 'object' && Object.keys(results as object).length > 0;
  return started ? 'Draft' : 'Not started';
}

/** `true` when reopening this row in the form is a resume rather than a new record. */
export function isResumableDraft(submission: { status?: string | null }): boolean {
  return submission.status === 'draft';
}

/**
 * The finding, for display. `overall_result` is constrained to three values.
 *
 * An absent result renders as an em dash, never as "pass". A draft has no
 * finding yet, and a submitted row without one is a gap worth seeing.
 */
export const INSPECTION_RESULT_LABELS: Record<string, string> = {
  pass: 'Pass',
  fail: 'Fail',
  conditional: 'Conditional',
};

export function inspectionResultLabel(submission: {
  overall_result?: string | null;
  overallResult?: string | null;
}): string {
  const key = submission.overall_result ?? submission.overallResult ?? '';
  if (!key) return '—';
  return INSPECTION_RESULT_LABELS[key] || key;
}
